import { Card } from "./ui/card";
import { hdImages } from "@/lib/hd-images";
import { Media } from "./Media";
import {

  AnimatedHoverCard,
  AnimatedHoverCardContent,
  AnimatedHoverCardTrigger,
} from "./ui/animated-hover-card";

export const HowItWorks = () => {
  const steps = [
    {
      num: "01",
      title: "Initiate",
      subtitle: "Start in seconds",
      description: "Launch a Code Blue session fast. Timers and prompts align with your local pathway from the first tap.",
      image: hdImages.howInitiate,
      cardHoverTitle: "Rapid Activation",
      cardHoverDescription: "One-touch activation starts the protocol timer immediately. No complex setup or login required during emergencies.",
      imageHoverTitle: "Emergency Ready Interface",
      imageHoverDescription: "Clean, high-contrast display designed for high-stress environments with large touch targets and clear visual hierarchy.",
    },
    {
      num: "02",
      title: "Guide",
      subtitle: "Context-aware prompts",
      description: "Cadenced cues for compressions, rhythm checks, meds, and airway—sequenced how your experts run a code.",
      image: hdImages.howContext,
      reverse: true,
      cardHoverTitle: "Intelligent Guidance",
      cardHoverDescription: "The software is configured to your hospital's protocols and reviewed expert practice, and is designed to present contextual prompts during an event for clinician review.",
      imageHoverTitle: "Real-Time Decision Support",
      imageHoverDescription: "Prompts are designed to reflect elapsed time and documented event inputs. They are advisory only; the clinician remains responsible for every intervention.",
    },
    {
      num: "03",
      title: "Document",
      subtitle: "Auto-captured timeline",
      description: "Actions and timestamps are captured for accurate review, QI, and EMR-ready export.",
      image: hdImages.howDocument,
      cardHoverTitle: "Automatic Documentation",
      cardHoverDescription: "Documented actions, medications, and interventions are designed to be timestamped as they are recorded, producing a structured record for clinician review and correction.",
      imageHoverTitle: "EMR Integration Ready",
      imageHoverDescription: "Standards-based export to record systems is planned and requires per-deployment configuration and validation. Time savings and error reduction have not been measured.",
    },
  ];

  return (
    <section className="py-24" id="how-it-works">
      <div className="container mx-auto px-6">
        <div className="eyebrow eyebrow-dot mb-4 reveal">System overview</div>
        <h2 className="section-title mb-3 reveal">How datasyteAI™ works</h2>
        <p className="text-muted-foreground mb-12 reveal">
          Built for clarity under pressure: assist the code leader, support the team, and produce a structured record for review. Capabilities described are in development for a pre-market system that is not FDA cleared or approved.
        </p>

        {steps.map((step, index) => (
          <div
            key={index}
            className={`grid lg:grid-cols-2 gap-8 items-stretch mb-12 ${
              step.reverse ? "" : ""
            }`}
          >
            {step.reverse ? (
              <>
                <AnimatedHoverCard openDelay={200} closeDelay={100}>
                  <AnimatedHoverCardTrigger asChild>
                    <div className="reveal order-2 lg:order-1 overflow-hidden rounded-xl shadow-soft cursor-pointer">
                      <Media
                        src={step.image}
                        alt={`${step.title}: ${step.subtitle} — AiMediQ™ Code Blue workflow step ${step.num}`}
                        className="w-full h-full object-cover min-h-[280px] hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 1024px) 100vw, 620px"
                      />
                    </div>
                  </AnimatedHoverCardTrigger>
                  <AnimatedHoverCardContent className="w-80 bg-card/95 backdrop-blur-sm border-accent/20">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-accent">{step.imageHoverTitle}</h4>
                      <p className="text-sm text-muted-foreground">{step.imageHoverDescription}</p>
                    </div>
                  </AnimatedHoverCardContent>
                </AnimatedHoverCard>
                <AnimatedHoverCard openDelay={200} closeDelay={100}>
                  <AnimatedHoverCardTrigger asChild>
                    <Card className="reveal p-8 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-center order-1 lg:order-2 border-l-4 border-l-accent cursor-pointer">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary-light to-primary-dark border border-primary/35 flex-shrink-0 mt-1 shadow-md" />
                        <div>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <strong className="text-xl font-bold text-primary">{step.num}</strong>
                            <strong className="text-lg">{step.title}</strong>
                            <span className="text-muted-foreground text-sm">— {step.subtitle}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </Card>
                  </AnimatedHoverCardTrigger>
                  <AnimatedHoverCardContent className="w-80 bg-card/95 backdrop-blur-sm border-accent/20">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-accent">{step.cardHoverTitle}</h4>
                      <p className="text-sm text-muted-foreground">{step.cardHoverDescription}</p>
                    </div>
                  </AnimatedHoverCardContent>
                </AnimatedHoverCard>
              </>
            ) : (
              <>
                <AnimatedHoverCard openDelay={200} closeDelay={100}>
                  <AnimatedHoverCardTrigger asChild>
                    <Card className="reveal p-8 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-center border-l-4 border-l-primary cursor-pointer">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary-light to-primary-dark border border-primary/35 flex-shrink-0 mt-1 shadow-md" />
                        <div>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <strong className="text-xl font-bold text-primary">{step.num}</strong>
                            <strong className="text-lg">{step.title}</strong>
                            <span className="text-muted-foreground text-sm">— {step.subtitle}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </Card>
                  </AnimatedHoverCardTrigger>
                  <AnimatedHoverCardContent className="w-80 bg-card/95 backdrop-blur-sm border-primary/20">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-primary">{step.cardHoverTitle}</h4>
                      <p className="text-sm text-muted-foreground">{step.cardHoverDescription}</p>
                    </div>
                  </AnimatedHoverCardContent>
                </AnimatedHoverCard>
                <AnimatedHoverCard openDelay={200} closeDelay={100}>
                  <AnimatedHoverCardTrigger asChild>
                    <div className="reveal overflow-hidden rounded-xl shadow-soft cursor-pointer">
                      <Media
                        src={step.image}
                        alt={`${step.title}: ${step.subtitle} — AiMediQ™ Code Blue workflow step ${step.num}`}
                        className="w-full h-full object-cover min-h-[280px] hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 1024px) 100vw, 620px"
                      />
                    </div>
                  </AnimatedHoverCardTrigger>
                  <AnimatedHoverCardContent className="w-80 bg-card/95 backdrop-blur-sm border-primary/20">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-primary">{step.imageHoverTitle}</h4>
                      <p className="text-sm text-muted-foreground">{step.imageHoverDescription}</p>
                    </div>
                  </AnimatedHoverCardContent>
                </AnimatedHoverCard>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
